<template>
  <div class="content ai-robot" @wheel="handleGlobalWheel">
    <h3 ref="pageHeader" class="pageHeader">AI{{ $store.state.themeTitle }}助手</h3>

    <!-- 页面顶部：智能体介绍 -->
    <div class="ai-intro">
      <img class="intro-img" :src="aiRobot" alt="智能体机器人" />
      <div class="intro-info">
        <div class="intro-title">AI{{ $store.state.themeTitle }}助手</div>
        <div class="intro-desc">
          您好，我是智能体助手。可以为您解答系统使用问题、实训室安全管理建议，并提醒待办事项，请随时提问。
        </div>
      </div>
    </div>

    <!-- 热门话题与最新通知（可折叠） -->
    <div class="ai-panels">
      <div class="modules-row">
        <!-- 热门问题卡片 -->
        <div class="module-card hot-module">
          <div class="card-title">
            <i class="el-icon-aim"></i>
            <span>热门问题</span>
          </div>
          <div class="card-body">
            <ul class="qa-list">
              <li v-for="(q, qi) in hotQuestions" :key="qi" @click="useHotQuestion(q)">
                <span class="qa-text">{{ q.question }}</span>
                <i class="el-icon-right"></i>
              </li>
              <li v-if="!hotQuestions.length" class="qa-empty">暂无热门问题</li>
            </ul>
          </div>
        </div>
        <!-- 最新通知卡片 -->
        <div class="module-card notice-module">
          <div class="card-title">
            <i class="el-icon-bell"></i>
            <span>最新通知</span>
          </div>
          <div class="card-body">
            <ul class="qa-list">
              <li v-for="(n, ni) in notices" :key="ni" @click="jump('/notice')">
                <span class="qa-text">{{ n.title }}</span>
                <span class="qa-time">{{ n.time }}</span>
              </li>
              <li v-if="!notices.length" class="qa-empty">暂无最新通知</li>
            </ul>
          </div>
        </div>
      </div>
      <!-- 折叠按钮：底部居中 -->
      <div class="collapse-card" @click="toggleAllPanels">
        <i class="el-icon-arrow-down" :class="{ 'rotate-180': panelOpen }"></i>
      </div>
    </div>

    <!-- 主体：左侧对话列表 + 右侧对话区 -->
    <div class="ai-body">
      <div class="ai-sidebar">
        <el-button icon="el-icon-plus" class="new-session-btn" @click="newSession">
          新建对话
        </el-button>
        <div class="session-list">
          <div v-for="item in sessions" :key="item.session_id" class="session-item"
            :class="{ active: item.session_id === currentSessionId }" @click="selectSession(item)">
            <i class="el-icon-chat-line-square session-icon"></i>
            <span class="session-title" :title="item.title">{{ item.title }}</span>
            <i v-if="!item.isNew" class="el-icon-delete session-del" @click.stop="deleteSession(item)"></i>
          </div>
          <el-empty v-if="!sessions.length" description="暂无对话" :image-size="60"></el-empty>
        </div>
      </div>

      <div class="ai-chat">
        <div ref="msgBox" class="chat-messages">
          <div v-for="(msg, index) in messages" :key="index" class="chat-msg" :class="msg.role">
            <div class="msg-avatar">
              <img v-if="msg.role === 'assistant'" :src="aiAvatar" alt="AI头像" />
              <img v-else :src="userAvatar" alt="用户头像" />
            </div>
            <div class="msg-bubble">
              <!-- 带操作按钮的待办消息用纯文本，保证按钮与文字同行；普通消息走 markdown 渲染 -->
              <p style="margin: 4px 0;">
                <span v-if="msg.content" class="msg-text" :class="{ plain: msg.actions && msg.actions.length }">
                  <template v-if="msg.actions && msg.actions.length">{{ msg.content }}</template>
                  <span v-else v-html="renderMarkdown(msg.content)"></span>
                </span>
                <span v-else-if="sending && index === messages.length - 1" class="typing">
                  <span class="thinking-text">{{ thinkingText }}</span>
                  <span class="typing-dots"><span></span><span></span><span></span></span>
                </span>
                <span v-if="msg.actions && msg.actions.length" class="msg-actions">
                  <el-button v-for="(action, ai) in msg.actions" :key="ai" class="msg-action-btn" type="text"
                    size="mini" @click="jump(action.path)">{{ action.label }}</el-button>
                </span>
              </p>
            </div>
          </div>
        </div>
        <!-- 移动端顶部工具条：历史记录入口 -->
        <div class="chat-toolbar">
          <i class="el-icon-chat-line-round chat-history-icon" @click="sessionDialogVisible = true" title="对话记录"></i>
        </div>
        <div class="chat-input-area">
          <div class="chat-input-box">
            <el-input v-model="input" type="textarea" :rows="3" resize="none"
              placeholder="请输入您的问题，Enter 发送，Shift+Enter 换行" @keydown.enter.native="onEnter"></el-input>
            <el-button class="send-btn" type="primary" circle :loading="sending" :disabled="!input.trim()"
              icon="el-icon-position" @click="send"></el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 对话记录弹窗 -->
    <el-dialog title="对话记录" :visible.sync="sessionDialogVisible" width="320px" append-to-body custom-class="ai-session-dialog">
      <el-button icon="el-icon-plus" class="dialog-new-btn" @click="newSession">新建对话</el-button>
      <div class="session-list">
        <div v-for="item in sessions" :key="item.session_id" class="session-item"
          :class="{ active: item.session_id === currentSessionId }" @click="selectSession(item)">
          <i class="el-icon-chat-line-square session-icon"></i>
          <span class="session-title" :title="item.title">{{ item.title }}</span>
          <i v-if="!item.isNew" class="el-icon-delete session-del" @click.stop="deleteSession(item)"></i>
        </div>
        <el-empty v-if="!sessions.length" description="暂无对话" :image-size="60"></el-empty>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import aiAvatar from "@/assets/image/ai-avatar.png";
import aiRobot from "@/assets/image/ai-robot.png";
import userAvatar from "@/assets/image/user.png";

export default {
  name: "AiRobot",
  data() {
    return {
      sessions: [],        // 会话列表
      currentSessionId: "", // 当前会话 id
      messages: [],        // 当前会话消息
      input: "",           // 输入框内容
      sending: false,      // 是否正在请求
      thinkingText: "",    // 模拟思考过程文案
      thinkingTimer: null, // 思考文案轮换定时器
      typingTimer: null,   // 打字机效果定时器
      replyTimer: null,    // 思考延时回复定时器
      aiAvatar,            // 智能体头像
      aiRobot,             // 智能体机器人图片
      userAvatar,          // 用户头像
      sessionDialogVisible: false, // 对话记录弹窗显隐
      panelOpen: false,    // 折叠面板展开状态（桌面端）
      hotQuestions: [],    // 热门话题列表
      notices: [],         // 最新通知列表
      todo: {              // 待办/预警数量
        hazard: 0,
        device: 0,
        consume: 0,
        chemical: 0,
        notice: 0
      }
    };
  },
  created() {
    // 打开页面默认创建新会话
    this.newSession();
    this.loadSessions();
    this.loadTodos();
    this.loadHotQuestions();
  },
  beforeDestroy() {
    if (this.thinkingTimer) {
      clearInterval(this.thinkingTimer);
      this.thinkingTimer = null;
    }
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }
    if (this.replyTimer) {
      clearTimeout(this.replyTimer);
      this.replyTimer = null;
    }
  },
  methods: {
    // 模拟思考步骤文案
    thinkingSteps() {
      return [
        "正在理解您的问题",
        "正在检索知识库",
        "正在分析实训室数据",
        "正在整理回答"
      ];
    },
    // 加载会话列表
    loadSessions() {
      this.$api.instance.interfaceServer("api/ai/chat/sessions", "get", {}).then((res) => {
        if (res && res.code == 0) {
          const serverList = this.parseSessions(res);
          // 保留本地新建、后端尚未保存的会话，并避免与服务端返回的会话重复
          const serverIds = serverList.map((s) => s.session_id);
          const localNew = this.sessions.filter((s) => s.isNew && serverIds.indexOf(s.session_id) === -1);
          this.sessions = localNew.concat(serverList);
        }
      }).catch(() => { });
    },
    // 解析会话列表，兼容多种返回结构
    parseSessions(res) {
      const data = res && res.data;
      const list = Array.isArray(data) ? data : (data && (data.list || data.data)) || [];
      return list
        .map((item) => ({
          session_id: item.session_id || item.id || item.sessionId,
          title: item.title || item.name || item.session_name || "未命名对话"
        }))
        .filter((item) => item.session_id);
    },
    // 新建会话
    newSession() {
      const id = `sess_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      this.currentSessionId = id;
      this.messages = [];
      // isNew 标记：本地新建、尚未被后端保存的会话
      this.sessions.unshift({ session_id: id, title: "新对话", isNew: true });
      // 新对话展示待办数据
      this.appendTodoMessages();
      this.sessionDialogVisible = false;
    },
    // 切换会话
    selectSession(item) {
      if (this.currentSessionId === item.session_id) return;
      this.currentSessionId = item.session_id;
      this.messages = [];
      this.sessionDialogVisible = false;
      this.loadMessages(item.session_id);
    },
    // 加载会话消息
    loadMessages(session_id) {
      this.$api.instance.interfaceServer(
        `api/ai/chat/sessions/${session_id}/messages`,
        "get",
        { per_page: 10, page: 1 }
      ).then((res) => {
        if (res && res.code == 0) {
          this.messages = this.parseMessages(res);
          // 内部判断仅新对话追加待办数据
          this.appendTodoMessages();
          this.scrollToBottom();
        }
      }).catch(() => { });
    },
    // 解析历史消息
    parseMessages(res) {
      const data = res && res.data;
      const list = Array.isArray(data) ? data : (data && (data.list || data.data)) || [];
      return list
        .map((item) => {
          const role = item.role === "user" || item.role === "assistant"
            ? item.role
            : (item.is_ai ? "assistant" : "user");
          return { role, content: item.content || item.message || item.text || "" };
        })
        .filter((item) => item.content);
    },
    // 删除会话
    deleteSession(item) {
      this.$confirm(`确定删除对话「${item.title}」吗？`, "提示", {
        type: "warning"
      }).then(() => {
        // 删除后若没有新对话，自动新建一个（展示待办数据）
        const removeLocal = () => {
          this.sessions = this.sessions.filter((s) => s.session_id !== item.session_id);
          // 删除的是当前会话时，先清空当前状态
          if (this.currentSessionId === item.session_id) {
            this.currentSessionId = "";
            this.messages = [];
          }
          // 有新的对话则切换到新对话窗口，没有则新建
          const newOne = this.sessions.find((s) => s.isNew);
          if (newOne) {
            this.selectSession(newOne);
          } else {
            this.newSession();
          }
        };
        // 本地新对话尚未保存到后端，直接本地删除
        if (item.isNew) {
          removeLocal();
          return;
        }
        this.$api.instance.interfaceServer(
          `api/ai/chat/sessions/${item.session_id}`,
          "delete",
          {}
        ).then((res) => {
          if (res && res.code == 0) {
            removeLocal();
          }
        }).catch(() => { });
      }).catch(() => { });
    },
    // 加载待办与预警数量
    loadTodos() {
      Promise.all([
        this.$api.instance.interfaceServer("api/ai/chat/checkCount", "get", {}),
        this.$api.instance.interfaceServer("api/ai/chat/deviceScrap", "get", {}),
        this.$api.instance.interfaceServer("api/ai/chat/consumeRecord", "get", {}),
        this.$api.instance.interfaceServer("api/ai/chat/chemicalRecord", "get", {}),
        this.$api.instance.interfaceServer("api/notice/list", "post", { page: 1, size: 10 })
      ]).then((results) => {
        this.todo.hazard = this.formatCount(results[0]);
        this.todo.device = this.formatCount(results[1]);
        this.todo.consume = this.formatCount(results[2]);
        this.todo.chemical = this.formatCount(results[3]);
        this.notices = this.parseNoticesList(results[4]);
        this.todo.notice = this.notices.length;
        this.appendTodoMessages();
      }).catch(() => { });
    },
    // 加载热门话题
    loadHotQuestions() {
      this.$api.instance.interfaceServer("api/agent/hot/questions/list", "get", { page: 1, size: 10 }).then((res) => {
        if (res && res.code == 0) {
          this.hotQuestions = this.parseHotQuestions(res);
        }
      }).catch(() => { });
    },
    // 解析热门话题，兼容字符串/对象、数组/分页结构
    // 解析热门话题，返回 { question, asking } 结构
    parseHotQuestions(res) {
      const data = res && res.data;
      let list = Array.isArray(data) ? data : (data && (data.list || data.data || data.questions)) || [];
      if (list && list.data && Array.isArray(list.data)) list = list.data;
      return list
        .map((item) => {
          if (typeof item === "string") {
            return { question: item, asking: "" };
          }
          return {
            question: item.question || item.title || item.name || item.content || item.text || "",
            asking: item.asking || item.answer || item.reply || item.content || ""
          };
        })
        .filter((item) => item.question);
    },
    // 点击热门话题：模拟思考后打字机输出标准回答（asking）
    useHotQuestion(item) {
      if (!item || !item.question || this.sending) return;
      this.messages.push({ role: "user", content: item.question });
      const aiMsg = { role: "assistant", content: "" };
      this.messages.push(aiMsg);
      this.scrollToBottom();
      this.sending = true;
      // 模拟思考过程
      const steps = this.thinkingSteps();
      this.thinkingText = steps[0];
      let step = 0;
      this.thinkingTimer = setInterval(() => {
        step = (step + 1) % steps.length;
        this.thinkingText = steps[step];
      }, 500);
      const answer = item.asking || "暂时没有相关回答，请尝试换个问题或直接提问。";
      // 思考约 1.5 秒后开始打字输出
      this.replyTimer = setTimeout(() => {
        this.replyTimer = null;
        if (this.thinkingTimer) {
          clearInterval(this.thinkingTimer);
          this.thinkingTimer = null;
        }
        this.typeAnswer(aiMsg, answer);
      }, 1500);
    },
    // 打字机效果：逐段输出 AI 回答
    typeAnswer(aiMsg, text) {
      let i = 0;
      this.typingTimer = setInterval(() => {
        i += 2;
        aiMsg.content = text.slice(0, i);
        this.scrollToBottom();
        if (i >= text.length) {
          clearInterval(this.typingTimer);
          this.typingTimer = null;
          this.sending = false;
          this.scrollToBottom();
        }
      }, 30);
    },
    // 统一切换面板折叠/展开（高度过渡动画）
    toggleAllPanels() {
      const bodies = this.$el.querySelectorAll(".card-body");
      if (this.panelOpen) {
        bodies.forEach((b) => {
          b.style.height = b.offsetHeight + "px";
          b.offsetHeight;
          b.style.height = "0px";
        });
        this.panelOpen = false;
      } else {
        bodies.forEach((b) => {
          b.style.height = "auto";
          const fullHeight = b.scrollHeight;
          b.style.height = "0px";
          b.offsetHeight;
          b.style.height = fullHeight + "px";
        });
        this.panelOpen = true;
      }
    },
    // 整页滚轮控制卡片折叠（桌面端）：向下滚折叠；向上滚到顶部展开
    handleGlobalWheel(e) {
      if (window.innerWidth <= 768) return;
      const panelsEl = this.$el.querySelector(".ai-panels");
      if (panelsEl && panelsEl.contains(e.target)) return;
      const deltaY = e.deltaY;
      if (deltaY > 0) {
        if (this.panelOpen) {
          this.toggleAllPanels();
        }
      } else if (deltaY < 0) {
        const chatArea = this.$refs.msgBox;
        const overChat = chatArea && chatArea.contains(e.target);
        if (overChat && chatArea.scrollTop > 0) return;
        if (!this.panelOpen) {
          this.toggleAllPanels();
        }
      }
    },
    // 解析通知列表，兼容数组 / list / Laravel 分页结构
    parseNoticesList(res) {
      const data = res && res.data;
      let list = Array.isArray(data) ? data : (data && (data.list || data.data || data.records)) || [];
      if (list && list.data && Array.isArray(list.data)) list = list.data;
      return list
        .map((item) => ({
          title: item.title || item.name || item.content || "",
          time: item.created_at || item.create_time || item.time || item.publish_time || ""
        }))
        .filter((item) => item.title);
    },
    // 把待办/预警组装成对话消息，追加到对话末尾
    appendTodoMessages() {
      // 仅新对话展示待办数据，切换历史对话不展示
      const current = this.sessions.find((s) => s.session_id === this.currentSessionId);
      if (!current || !current.isNew) return;
      // 待办类型：数量、文案、对应处理页面
      const items = [
        { count: this.todo.hazard, text: "安全隐患", verb: "待处理，请及时进行整改", path: "/troubles" },
        { count: this.todo.device, text: "仪器设备报废申请审批", verb: "待处理，请及时处理", path: "/equipmentScrapOper" },
        { count: this.todo.consume, text: "耗材领用申请审批", verb: "待处理，请及时处理", path: "/consumableBorrowOper" },
        { count: this.todo.chemical, text: "化学品领用审批", verb: "待处理，请及时处理", path: "/chemicalBorrowOper" },
        { count: this.todo.notice, text: "最新通知", verb: "待查看，请及时查看", path: "/notice" }
      ];
      items.forEach((item) => {
        if (item.count > 0) {
          this.messages.push({
            role: "assistant",
            content: `当前有 ${item.count}条${item.text}${item.verb}！`,
            actions: [{ label: "现在处理", path: item.path }]
          });
        }
      });
    },
    // 跳转到对应处理页面
    jump(path) {
      this.$router.push(path);
    },
    // 兼容数字 / 字符串 / 数组 / 对象多种返回
    formatCount(res) {
      const data = res && res.data;
      if (data == null) return 0;
      if (typeof data === "number") return data;
      if (typeof data === "string") return parseInt(data, 10) || 0;
      if (Array.isArray(data)) return data.length;
      if (typeof data === "object") {
        return data.count || data.total || data.num || 0;
      }
      return 0;
    },
    // Enter 发送，Shift+Enter 换行
    onEnter(e) {
      if (e.shiftKey) return;
      e.preventDefault();
      this.send();
    },
    // 流式对话请求：显式调用，读取 SSE 流
    requestChatStream(body) {
      const token = (this.$store.state.userInfo && this.$store.state.userInfo.userToken) ||
        this.$store.state.token || "";
      return fetch("/api/ai/chat-stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Token": token
        },
        body: JSON.stringify(body)
      });
    },
    // 发送消息（SSE 流式）
    async send() {
      const message = this.input.trim();
      if (!message || this.sending) return;
      if (!this.currentSessionId) {
        this.newSession();
      }
      // 是否为本地新建会话（首次提问后需要刷新会话列表）
      const isNewSession = this.sessions.some((s) => s.session_id === this.currentSessionId && s.isNew);
      this.messages.push({ role: "user", content: message });
      const aiMsg = { role: "assistant", content: "" };
      this.messages.push(aiMsg);
      this.input = "";
      this.sending = true;
      // 启动模拟思考：轮换显示思考步骤
      const steps = this.thinkingSteps();
      this.thinkingText = steps[0];
      let step = 0;
      this.thinkingTimer = setInterval(() => {
        step = (step + 1) % steps.length;
        this.thinkingText = steps[step];
      }, 1500);
      this.scrollToBottom();
      try {
        const res = await this.requestChatStream({
          message,
          session_id: this.currentSessionId
        });
        if (!res.ok) {
          throw new Error(`请求失败：${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        for (; ;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop();
          lines.forEach((line) => this.handleSseLine(line, aiMsg));
          // 收到首个内容片段后停止思考过程
          if (aiMsg.content && this.thinkingTimer) {
            clearInterval(this.thinkingTimer);
            this.thinkingTimer = null;
          }
          this.scrollToBottom();
        }
        if (buffer.trim()) {
          this.handleSseLine(buffer, aiMsg);
        }
        if (!aiMsg.content) {
          aiMsg.content = "（没有收到回复内容）";
        }
      } catch (e) {
        aiMsg.content = "抱歉，请求出错了，请稍后重试。";
      } finally {
        if (this.thinkingTimer) {
          clearInterval(this.thinkingTimer);
          this.thinkingTimer = null;
        }
        this.sending = false;
        if (isNewSession) {
          this.loadSessions();
        }
        this.scrollToBottom();
      }
    },
    // 轻量 markdown 渲染：先转义 HTML 防止 XSS，再转换常用语法
    renderMarkdown(text) {
      if (!text) return "";
      const esc = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const lines = esc.split(/\r?\n/);
      let html = "";
      let inCode = false;
      let codeLines = [];
      let listBuf = [];
      const flushList = () => {
        if (listBuf.length) {
          html += "<ul>" + listBuf.map((l) => "<li>" + l + "</li>").join("") + "</ul>";
          listBuf = [];
        }
      };
      lines.forEach((line) => {
        if (/^```/.test(line.trim())) {
          if (inCode) {
            html += "<pre><code>" + codeLines.join("\n") + "</code></pre>";
            codeLines = [];
            inCode = false;
          } else {
            flushList();
            inCode = true;
          }
          return;
        }
        if (inCode) {
          codeLines.push(line);
          return;
        }
        // 标题
        let m = line.match(/^(#{1,4})\s+(.*)$/);
        if (m) {
          flushList();
          const level = m[1].length + 1;
          html += "<h" + level + ">" + this.inlineMarkdown(m[2]) + "</h" + level + ">";
          return;
        }
        // 无序列表
        m = line.match(/^\s*[-*]\s+(.*)$/);
        if (m) {
          listBuf.push(this.inlineMarkdown(m[1]));
          return;
        }
        // 有序列表
        m = line.match(/^\s*\d+[.、]\s+(.*)$/);
        if (m) {
          listBuf.push(this.inlineMarkdown(m[1]));
          return;
        }
        flushList();
        if (line.trim() === "") return;
        html += "<p>" + this.inlineMarkdown(line) + "</p>";
      });
      if (inCode) {
        html += "<pre><code>" + codeLines.join("\n") + "</code></pre>";
      }
      flushList();
      return html;
    },
    // 行内语法：行内代码、加粗、斜体、链接
    inlineMarkdown(str) {
      return str
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    },
    // 解析单行 SSE 数据
    handleSseLine(line, aiMsg) {
      const text = line.trim();
      if (!text || text === "[DONE]") return;
      let content = "";
      if (text.startsWith("data:")) {
        const payload = text.slice(5).trim();
        if (!payload || payload === "[DONE]") return;
        try {
          const obj = JSON.parse(payload);
          if (typeof obj === "string") {
            content = obj;
          } else {
            content = obj.content || obj.delta || obj.message || obj.text || obj.data || "";
            if (typeof content !== "string") content = "";
          }
        } catch (e) {
          content = payload;
        }
      } else {
        content = text;
      }
      if (content) {
        aiMsg.content += content;
      }
    },
    // 滚动到底部
    scrollToBottom() {
      this.$nextTick(() => {
        const box = this.$refs.msgBox;
        if (box) {
          box.scrollTop = box.scrollHeight;
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
/deep/.el-button.is-circle {
  padding: 10px 10px 8px 8px;
}

/deep/.el-icon-position:before {
  font-size: 20px;
}

.ai-robot {
  // 覆盖全局 .content：撑满可视区域，无滚动条
  display: flex;
  flex-direction: column;
  height: calc(100vh - 98px);
  box-sizing: border-box;
  overflow: hidden;
  background: linear-gradient(180deg, #F3F9FD 0%, #F3F4FD 100%), radial-gradient(130.25% 45.81% at -90.33% 1.14%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
  padding: 22px 22px 8px;
  margin: 16px 16px 8px;
  .ai-intro {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    .intro-img {
      width: 72px;
      height: 72px;
      object-fit: contain;
    }

    .intro-info {
      .intro-title {
        font-size: 24px;
        font-weight: 800;
        color: #1f2a44;
        letter-spacing: -0.3px;
        margin-bottom: 4px;
      }

      .intro-desc {
        font-size: 14px;
        color: #5a6e8a;
      }
    }
  }

  .ai-panels {
    margin-bottom: 4px;

    // 两个模块并排
    .modules-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0 10px;
    }

    .module-card {
      display: flex;
      flex-direction: column;
      padding: 8px 12px;
      border-radius: 4px;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.03);
      transition: transform 0.2s;

      .card-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 4px;
        color: #1e2f4e;

        i {
          font-size: 20px;
        }

      }

      // 内容区：高度过渡折叠
      .card-body {
        height: 0;
        overflow: hidden;
        transition: height 0.3s ease-out;
      }
    }

    // 热门问题：暖橙红渐变
    .hot-module {
      background: linear-gradient(178deg, #ffb476 0%, rgba(255, 236, 220, 0.63) 29.69%, #fffefe 97.14%),
        radial-gradient(125.07% 54.55% at -19.48% 0.09%, #ff8370 0%, rgba(255, 255, 255, 0) 100%);

      .card-title i {
        color: #ff6b3d;
      }
    }

    // 最新通知：蓝白渐变
    .notice-module {
      background: radial-gradient(135.8% 489.68% at 53.15% -22.13%, #d9e3f7 30.03%, rgba(255, 255, 255, 0) 100%),
        radial-gradient(130.89% 249.29% at 60.85% 115.89%, #d8eafb 30.84%, rgba(255, 255, 255, 0) 100%), #eef5fd;

      .card-title i {
        color: #3f7eff;
      }
    }

    // 问题/通知列表
    .qa-list {
      margin: 0;
      padding: 2px 4px;
      list-style: none;
      background: rgba(255, 255, 245, 0.7);
      border-radius: 4px;
      overflow: hidden;
      max-height: 120px;
      overflow-y: auto;

      li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        font-size: 13px;
        color: #1f2937;
        cursor: pointer;
        transition: background 0.2s;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.55);
        }

        .qa-text {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        i {
          color: #ff884d;
          font-size: 14px;
          margin-left: 8px;
        }

        .qa-time {
          margin-left: 8px;
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
        }
      }

      .qa-empty {
        justify-content: center;
        color: #94a3b8;
        cursor: default;
      }
    }

    // 折叠按钮：底部居中条（桌面端）
    .collapse-card {
      border-top: 3px solid #fff;
      position: relative;
      text-align: center;

      &::before {
        content: "";
        display: block;
        width: 80px;
        height: 16px;
        line-height: 17px;
        margin: 0 auto;
        text-align: center;
        background: #d6e2f3;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        cursor: pointer;
      }

      i {
        font-size: 12px;
        color: #5a6e8a;
        position: absolute;
        top: 2px;
        left: 50%;
        transform: translateX(-50%);
        transition: transform 0.3s;

        &.rotate-180 {
          transform: translateX(-50%) rotate(180deg);
        }
      }
    }

  }

  .ai-body {
    display: flex;
    flex: 1;
    min-height: 0;
    // border: 1px solid #e4e7ed;
    border-radius: 6px;
    overflow: hidden;

    // 桌面端：左侧会话列表
    .ai-sidebar {
      display: flex;
      flex-direction: column;
      width: 240px;
      border-right: 1px solid #e4e7ed;

      .new-session-btn {
        margin: 12px;
        border-radius: 8px;
        border: none;
        color: #303133;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: all 0.2s;

        &:hover {
          background: #f5f7fa;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }
      }

      .session-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 0 8px 8px;

        .session-item {
          display: flex;
          align-items: center;
          padding: 9px 12px;
          margin-bottom: 6px;
          border-radius: 8px;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;

          &:hover {
            background: #f5f7fa;
            border-color: #e4e7ed;
          }

          &.active {
            background: #ecf5ff;
            border-color: #b3d8ff;

            .session-icon {
              color: #409eff;
            }

            .session-title {
              color: #409eff;
              font-weight: 600;
            }
          }

          .session-icon {
            margin-right: 6px;
            color: #909399;
          }

          .session-title {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 13px;
            color: #303133;
          }

          .session-del {
            display: none;
            color: #f56c6c;
          }

          &:hover .session-del {
            display: inline-block;
          }
        }
      }
    }

    // 对话区顶部工具条（移动端显示）
    .chat-toolbar {
      display: none;
      align-items: center;
      height: 22px;
      padding: 0 12px;
      flex-shrink: 0;

      .chat-history-icon {
        font-size: 20px;
        color: #5a6e8a;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #3f7eff;
        }
      }
    }

    .ai-chat {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      // background: #fff;

      .chat-messages {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        padding: 16px;
        overflow-y: auto;
        scroll-behavior: smooth;

        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
        }

        .chat-msg {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          max-width: 80%;
          margin-bottom: 16px;
          animation: ai-msg-fade-in 0.2s ease;

          &.user {
            flex-direction: row-reverse;
            align-self: flex-end;

            .msg-bubble {
              background: #3f7eff;
              color: #fff;
            }
          }

          .msg-avatar {
            flex-shrink: 0;
            width: 38px;
            height: 38px;
            border-radius: 50%;
            overflow: hidden;
            background: transparent;
            color: #fff;
            font-size: 13px;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }

          .msg-bubble {
            max-width: 100%;
            padding: 8px 12px;
            border-radius: 4px;
            background: #fff;
            color: #1e2a3a;
            border: 0.5px solid rgba(0, 0, 0, 0.03);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
            font-size: 14px;
            line-height: 1.45;
            word-break: break-word;
            white-space: normal;

            .msg-text {
              &.plain {
                white-space: pre-wrap;
              }

              /deep/ p {
                margin: 4px 0;
              }

              /deep/ h2,
              /deep/ h3,
              /deep/ h4 {
                margin: 8px 0 4px;
                font-weight: 600;
              }

              /deep/ ul {
                margin: 4px 0;
                padding-left: 20px;
              }

              /deep/ pre {
                background: #f6f8fa;
                border-radius: 6px;
                padding: 8px 10px;
                overflow-x: auto;
                margin: 6px 0;
                white-space: pre;
              }

              /deep/ code {
                background: #f6f8fa;
                border-radius: 3px;
                padding: 1px 4px;
                font-family: Consolas, Menlo, monospace;
                font-size: 12px;
                color: #d14;
              }

              /deep/ pre code {
                background: transparent;
                padding: 0;
                color: #333;
              }

              /deep/ a {
                color: #409eff;
                text-decoration: none;
              }
            }

            .msg-actions {
              display: inline;
              margin-left: 6px;

              .msg-action-btn {
                font-size: 12px;
                padding: 0;
              }
            }
          }

          .typing {
            display: inline-flex;
            align-items: center;
            color: #909399;
            font-size: 13px;

            .thinking-text {
              margin-right: 8px;
            }

            .typing-dots {
              display: inline-flex;
              align-items: center;

              span {
                display: inline-block;
                width: 6px;
                height: 6px;
                margin: 0 2px;
                border-radius: 50%;
                background: #909399;
                animation: typing-blink 1.2s infinite;

                &:nth-child(2) {
                  animation-delay: 0.2s;
                }

                &:nth-child(3) {
                  animation-delay: 0.4s;
                }
              }
            }
          }
        }
      }

      .chat-input-area {
        padding: 12px 16px;
        // border-top: 1px solid #e4e7ed;
        // background: #fff;

        .chat-input-box {
          position: relative;

          /deep/ .el-textarea__inner {
            padding: 10px 46px 10px 12px;
            border-radius: 10px;
            border-color: #dcdfe6;
            font-size: 14px;
            line-height: 1.6;
            transition: all 0.2s;

            &:hover {
              border-color: #c0c4cc;
            }

            &:focus {
              border-color: #409eff;
              box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
            }
          }

          .send-btn {
            position: absolute;
            right: 10px;
            bottom: 10px;
            z-index: 1;
          }
        }
      }
    }

  }
}

@keyframes typing-blink {

  0%,
  80%,
  100% {
    opacity: 0.3;
  }

  40% {
    opacity: 1;
  }
}

@keyframes ai-msg-fade-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .ai-robot {
    height: auto;
    min-height: calc(100vh - 60px);
    margin: 0;
    padding: 12px;

    .ai-intro {
      .intro-img {
        width: 52px;
        height: 52px;
      }

      .intro-title {
        font-size: 18px;
      }

      .intro-desc {
        font-size: 12px;
      }
    }

    .ai-panels {
      .modules-row {
        grid-template-columns: 1fr;
        gap: 10px 0;
      }

      .qa-list {
        max-height: 100px;
      }

      // 移动端：隐藏折叠按钮，卡片常显
      .collapse-card {
        display: none;
      }

      .module-card .card-body {
        height: auto;
        transition: none;
      }
    }

    .ai-body {
      flex-direction: column;
      height: calc(100vh - 400px);
      min-height: 300px;

      // 移动端：隐藏左侧会话列表，用弹窗代替
      .ai-sidebar {
        display: none;
      }

      // 移动端：显示历史记录图标
      .chat-toolbar {
        display: flex;
      }

      .ai-chat {
        .chat-messages {
          padding: 12px 4px;

          .chat-msg {
            max-width: 90%;
          }
        }

        .chat-input-area {
          padding: 8px 10px;
        }
      }
    }
  }
}
</style>

<style lang="less">
/* 对话记录弹窗（append-to-body，需全局样式） */
.ai-session-dialog {
  .dialog-new-btn {
    width: 100%;
    margin-bottom: 10px;
    border-radius: 8px;
    border: none;
    color: #303133;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.2s;

    &:hover {
      background: #f5f7fa;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
  }

  .session-list {
    max-height: 420px;
    overflow-y: auto;

    .session-item {
      display: flex;
      align-items: center;
      padding: 9px 12px;
      margin-bottom: 6px;
      border-radius: 8px;
      cursor: pointer;
      border: 1px solid transparent;
      transition: all 0.2s;

      &:hover {
        background: #f5f7fa;
        border-color: #e4e7ed;
      }

      &.active {
        background: #ecf5ff;
        border-color: #b3d8ff;

        .session-icon {
          color: #409eff;
        }

        .session-title {
          color: #409eff;
          font-weight: 600;
        }
      }

      .session-icon {
        margin-right: 6px;
        color: #909399;
      }

      .session-title {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
        color: #303133;
      }

      .session-del {
        display: none;
        color: #f56c6c;
      }

      &:hover .session-del {
        display: inline-block;
      }
    }
  }
}
</style>
